import { useMemo } from "react";

import * as Sentry from "@sentry/react";
import { useIntl } from "react-intl";
import { Outlet, useParams } from "react-router-dom";

import { AtlassianAdminIcon } from "@atlaskit/logo";
import Lozenge from "@atlaskit/lozenge-next";
import { Main } from "@atlaskit/navigation-system/layout/main";
import { Root } from "@atlaskit/navigation-system/layout/root";
import {
    SideNav,
    SideNavHeader
} from "@atlaskit/navigation-system/layout/side-nav";
import { SideNavContent } from "@atlaskit/navigation-system/layout/side-nav";
import { SideNavToggleButton } from "@atlaskit/navigation-system/layout/side-nav";
import {
    TopNav,
    TopNavStart,
    TopNavEnd
} from "@atlaskit/navigation-system/layout/top-nav";
import { LinkMenuItem } from "@atlaskit/navigation-system/side-nav-items/link-menu-item";
import { MenuList } from "@atlaskit/navigation-system/side-nav-items/menu-list";
import { AppLogo } from "@atlaskit/navigation-system/top-nav-items";
import { Box, Inline, xcss } from "@atlaskit/primitives-next";

import { AIChatWidget } from "@/components/fastshift/src/common/ai-chat-widget";
import { NavigationProfileV2 } from "@/components/fastshift/src/common/fastshift-navigation";
import { FeedbackCollector } from "@/components/fastshift/src/common/feedback-collector";
import { checkFeatureFlagGate } from "@/utils/feature-flags/check-feature-flag";
import { featureFlags } from "@/utils/feature-flags/feature-flag-manifest";

import { SENTRY_PROJECT_NAME } from "../../constants";
import { GenericErrorDisplay } from "../displays";
import { FASTSHIFT_HOME_PATH } from "../routes/constants";

import { AccessControlledPageContent } from "./access-controlled-page-content";
import { DomainScopedNavHeader } from "./domain-scoped-nav-header";
import { useLoadDomainScopedState } from "./hooks";
import { usePageNavLinks } from "./hooks";
import { usePageLayoutAnalytics } from "./hooks";
import {
    usePortalAuthenticationAndAccessControl,
    UserAccessValidationState
} from "./hooks/use-portal-authentication-and-access-control";
import { messages } from "./messages";

const mainStyles = xcss({
    padding: "space.200"
});

// Adapter to convert BaseLogoProps to LogoProps for AppLogo
// resolves incompatible typing issues from package versioning
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const AtlassianAdminIconAdapter = (props: any) => (
    <AtlassianAdminIcon {...props} />
);

const staticValueForTestingOnly = {
    userAccessState: UserAccessValidationState.VALIDATION_SUCCESS,
    shouldShowSideNav: true,
    isPartnerOrInternal: true
};

export const PageLayout = () => {
    const { domainId } = useParams<{ domainId: string }>();

    const isDisableAuthHookForTestingOnlyEnabled = checkFeatureFlagGate(
        featureFlags.sxpFastshiftPortalDisableAuthHookTestingOnly
    );
    const isFastshiftChatWidgetEnabled = checkFeatureFlagGate(
        featureFlags.sxpFastshiftPortalChatAgent
    );
    const isCustomerFeedbackEnabled = checkFeatureFlagGate(
        featureFlags.sxpFastshiftPortalEnableCustomerFeedback
    );

    const accessControlState = usePortalAuthenticationAndAccessControl();
    const { userAccessState, shouldShowSideNav, isPartnerOrInternal } =
        isDisableAuthHookForTestingOnlyEnabled
            ? staticValueForTestingOnly
            : accessControlState;

    const shouldDisplayAIChatWidget =
        isFastshiftChatWidgetEnabled &&
        !accessControlState.isOnHomeRoute &&
        (accessControlState.userAccessState ===
            UserAccessValidationState.VALIDATION_SUCCESS ||
            isDisableAuthHookForTestingOnlyEnabled);

    const shouldDisplayFeedbackCollector = useMemo(() => {
        return (
            isCustomerFeedbackEnabled &&
            accessControlState.userAccessState ===
                UserAccessValidationState.VALIDATION_SUCCESS
        );
    }, [isCustomerFeedbackEnabled, accessControlState]);

    useLoadDomainScopedState(domainId || "");

    const {
        pageKey,
        links: pageMenuItems,
        isOnDomainScopedPage
    } = usePageNavLinks();

    const { sendSideNavLinkClickEvent } = usePageLayoutAnalytics();

    const { formatMessage } = useIntl();

    return (
        <Sentry.ErrorBoundary
            beforeCapture={(scope) => {
                scope.setTag("projectName", SENTRY_PROJECT_NAME);
            }}
            fallback={<GenericErrorDisplay />}
        >
            <Root>
                <TopNav>
                    <TopNavStart>
                        {shouldShowSideNav && (
                            <SideNavToggleButton
                                defaultCollapsed
                                collapseLabel={formatMessage(
                                    messages.collapseSidebarLabel
                                )}
                                expandLabel={formatMessage(
                                    messages.expandSidebarLabel
                                )}
                            />
                        )}
                        <AppLogo
                            icon={AtlassianAdminIconAdapter}
                            name={formatMessage(messages.fastshiftAppName)}
                            label={formatMessage(messages.homePageLabel)}
                            href={FASTSHIFT_HOME_PATH}
                        />
                        <Lozenge appearance="removed">
                            {formatMessage(messages.betaLozenge)}
                        </Lozenge>
                    </TopNavStart>

                    <TopNavEnd>
                        {shouldDisplayFeedbackCollector && (
                            <FeedbackCollector />
                        )}
                        <NavigationProfileV2 />
                    </TopNavEnd>
                </TopNav>
                {shouldShowSideNav && (
                    <SideNav>
                        {isPartnerOrInternal && isOnDomainScopedPage && (
                            <SideNavHeader>
                                <DomainScopedNavHeader
                                    isOnDomainScopedPage={isOnDomainScopedPage}
                                />
                            </SideNavHeader>
                        )}
                        {/* Nav Links */}
                        <SideNavContent>
                            <MenuList>
                                {pageMenuItems.map((navItem) => {
                                    return (
                                        <LinkMenuItem
                                            key={navItem.key}
                                            href={navItem.link || ""}
                                            elemBefore={navItem.icon}
                                            isSelected={navItem.key == pageKey}
                                            onClick={() => {
                                                sendSideNavLinkClickEvent(
                                                    navItem.label,
                                                    navItem.link || "",
                                                    isOnDomainScopedPage
                                                );
                                            }}
                                        >
                                            {navItem.label}
                                        </LinkMenuItem>
                                    );
                                })}
                            </MenuList>
                        </SideNavContent>
                    </SideNav>
                )}
                <Main testId="main" id="main">
                    <Inline alignInline="center">
                        {shouldDisplayAIChatWidget && <AIChatWidget />}
                        <AccessControlledPageContent
                            userAccessState={userAccessState}
                        >
                            <Box xcss={mainStyles}>
                                <Outlet />
                            </Box>
                        </AccessControlledPageContent>
                    </Inline>
                </Main>
            </Root>
        </Sentry.ErrorBoundary>
    );
};
